import { position } from "polished";
import * as React from "react";
import styled from "styled-components";
import { hover } from "~/shared/styles/helpers";
import UnstyledButton from "./UnstyledButton";
import Input from "./forms/Input";

const Form = styled.form<FormProps>`
  max-width: 320px;
  padding: 24px 0 ${(props) => (props.noBottomSpace ? 0 : "40px")};
`;

interface FormProps extends React.HTMLAttributes<HTMLFormElement> {
  noBottomSpace?: boolean;
}

const SearchInput = styled(Input)`
  input {
    padding-left: 32px;
    background-image: url("/images/search-icon.svg");
    background-position: 8px center;
    background-size: 15px;
    background-repeat: no-repeat;
  }
`;

const ClearSearch = styled(UnstyledButton)`
  ${position("absolute", 0, 0, null, null)}
  padding: 10px 8px;
  color: var(--gray-text);

  ${hover(`
    color: var(--primary-green);
  `)}

  svg {
    display: block;
  }
`;

/**
 * @returns {object} Search
 * @returns {string} Search.searchTerm
 * @returns {function} Search.searchSubmit
 */
export const useSearch = () => {
  const [submittedSearchTerm, setSearchTerm] = React.useState<string | false>();

  const searchSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault();
      const { currentTarget: target } = event;
      const { value } = target.querySelector("input") ?? {};

      if (value === "") return;

      setSearchTerm(value);
    } else {
      setSearchTerm(false);
    }
  };

  return {
    searchTerm: submittedSearchTerm,
    searchSubmit,
  };
};

interface SearchProps extends FormProps {
  label: string;
  inputClassName?: string;
  // Not a fan of this but trying to maintain the existing API
  onSubmit?: ReturnType<typeof useSearch>["searchSubmit"];
}

const Search = ({ onSubmit = () => {}, onFocus, label, className, inputClassName, noBottomSpace }: SearchProps) => {
  const [inputValue, setInputValue] = React.useState("");

  return (
    <Form onSubmit={onSubmit} className={className} noBottomSpace={noBottomSpace}>
      <div style={{ position: "relative" }}>
        <SearchInput
          // @ts-expect-error I believe this is an issue caused by the way styled-components handles attrs
          type="search"
          placeholder="Search..."
          name="search"
          label={label}
          autoComplete="off"
          small
          hideLabel
          className={inputClassName}
          value={inputValue}
          onFocus={onFocus}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            setInputValue(val);
            if (val === "") {
              onSubmit();
            }
          }}
        />
        {inputValue !== "" ? (
          <ClearSearch
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setInputValue("");
              onSubmit();
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
              <path
                fill="currentColor"
                fillRule="evenodd"
                d="M8 2.6667C5.0545 2.6667 2.6666 5.0545 2.6666 8c0 2.9455 2.3879 5.3333 5.3334 5.3333S13.3333 10.9455 13.3333 8c0-2.9455-2.3878-5.3333-5.3333-5.3333zM1.3333 8c0-3.682 2.9848-6.6667 6.6667-6.6667 3.6818 0 6.6666 2.9848 6.6666 6.6667 0 3.6819-2.9848 6.6667-6.6666 6.6667-3.682 0-6.6667-2.9848-6.6667-6.6667zm3.862-2.8047a.6667.6667 0 01.9428 0L8 7.0572l1.862-1.862a.6666.6666 0 11.9427.9429L8.9427 8l1.862 1.862a.6667.6667 0 01-.9428.9427L8 8.9427l-1.8619 1.862a.6666.6666 0 11-.9428-.9428L7.0572 8l-1.862-1.862a.6667.6667 0 010-.9427z"
                clipRule="evenodd"
              />
            </svg>
          </ClearSearch>
        ) : null}
      </div>
    </Form>
  );
};

export default Search;
